import { getHotelByName } from "app/services/hotel/hotelService";
import queryStringService from "app/services/queryStringService";
import { useCallback, useRef, useState } from "react";
import AsyncSelect from "react-select/async";

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;

const HotelAutoComplete = ({ values, setFieldValue }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const debounceTimer = useRef(null);

  // ----------------------------------
  // Utility: Extract Postal Code
  // ----------------------------------
  const extractPostalCode = (address) => {
    const match = address?.match(/(\d{6})(?!.*\d)/);
    return match ? match[1] : null;
  };

  // ----------------------------------
  // API Fetch (Safe Guard)
  // ----------------------------------
  const fetchHotels = async (inputValue) => {
    const cityName = values?.searchCriteria?.cityName;

    // ⛔ Stop if city not selected
    if (!cityName) return [];

    const searchText = inputValue?.trim()?.toLowerCase();
    if (!searchText || searchText.length < MIN_SEARCH_LENGTH) return [];

    const query = queryStringService.getQueryStringFromObj({
      searchText,
    });

    try {
      const { data } = await getHotelByName(cityName, query);

      return (
        data?.data?.map((item) => ({
          id: item.hotelId,
          label: item.hotelName,
          hotelDescription: item.hotelDescription,
          hotelRating: Number(item.hotelRating),
          gl: {
            ln: item.longitude,
            lt: item.latitude,
          },
          address: {
            adr: item.hotelAddress,
            postalCode: extractPostalCode(item.hotelAddress),
            city: { name: item.cityName },
            state: { name: item.stateName },
            country: {
              code: item.countryId,
              name: item.countryName,
            },
            ctn: item.cityName,
            sn: item.stateName,
            cn: item.countryName,
          },
        })) || []
      );
    } catch (error) {
      console.error("Error loading hotels:", error);
      return [];
    }
  };

  // ----------------------------------
  // Debounced Load Options
  // ----------------------------------
  const loadOptions = useCallback(
    (inputValue) => {
      return new Promise((resolve) => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(async () => {
          const results = await fetchHotels(inputValue);
          resolve(results);
        }, DEBOUNCE_DELAY);
      });
    },
    [values?.searchCriteria?.cityName]
  );

  // ----------------------------------
  // Handle Selection
  // ----------------------------------
  const handleChange = (value) => {
    if (!value) return;

    setSelectedValue(value);
    setFieldValue("hotelName", value.label);
    setFieldValue("des", value.hotelDescription);
    setFieldValue("rating", value.hotelRating);
    setFieldValue("gl", value.gl);
    setFieldValue("address", value.address);
  };

  const isCitySelected = Boolean(values?.searchCriteria?.cityName);

  // ----------------------------------
  // Render
  // ----------------------------------
  return (
    <AsyncSelect
      cacheOptions
      value={selectedValue}
      loadOptions={loadOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.id}
      placeholder={isCitySelected ? "Select Hotel Name" : "Select City First"}
      isDisabled={!isCitySelected}
      isClearable
    />
  );
};

export default HotelAutoComplete;
